import { createLogger } from '@/shared/logging/logger'
import {
  LOCAL_INFERENCE_UNLOADED_MESSAGE,
  localInferenceRuntimeRegistry,
  useLocalInferenceStore,
} from '@/shared/state/local-inference'
import { getHuggingFaceMirrorBaseUrl } from '@/shared/utils/model-mirror'

const logger = createLogger('MossTtsService')

const HOST_SOURCE = 'freecut-moss-tts-worker'
const IFRAME_HOST_SOURCE = 'freecut-moss-tts-host'
const CLIENT_SOURCE = 'freecut-moss-tts-client'
const WORKER_PATH = '/moss-tts/moss_tts.worker.js'
const HOST_IFRAME_PATH = '/moss-tts/browser_onnx_host.html'
const MODEL_KEY = 'nano-zh'
const MODEL_LABEL = ''
const ESTIMATED_BYTES = 785_000_000
const THREAD_COUNT = 4
const HOST_READY_TIMEOUT_MS = 30_000
const WORKER_READY_TIMEOUT_MS = 30_000

function buildMossAssetUrl(path: string): string {
  const mirrorBaseUrl = getHuggingFaceMirrorBaseUrl()
  if (!mirrorBaseUrl) {
    return path
  }

  const separator = path.includes('?') ? '&' : '?'
  return `${path}${separator}hf_mirror_base_url=${encodeURIComponent(mirrorBaseUrl)}`
}

interface SerializedAudioChunk {
  sampleRate: number
  channels: number
  isPause?: boolean
  buffers: ArrayBuffer[]
}

interface ProgressInfo {
  type: 'progress'
  requestId: string
  stage?: string
}

interface ResponseInfo {
  type: 'response'
  requestId: string
  ok: boolean
  error?: string
  data?: {
    status?: string
    audioChunks?: SerializedAudioChunk[]
    textChunkCount?: number
  }
}

interface ReadyInfo {
  type: 'ready'
}

interface GenerateSpeechOptions {
  text: string
  voice: MossTtsVoice
  speed: number
  onProgress?: (stage: string) => void
}

interface GenerateVoiceCloneOptions {
  text: string
  speed: number
  referenceAudio: File
  onProgress?: (stage: string) => void
}

interface PendingRequest<T> {
  onProgress?: (stage: string) => void
  reject: (reason?: unknown) => void
  resolve: (value: T) => void
}

export const MOSS_TTS_VOICE_OPTIONS = [
  { value: 'Junhao', label: '男声-沉稳（Junhao）' },
  { value: 'Zhiming', label: '男声-专业（Zhiming）' },
  { value: 'Weiguo', label: '男声-磁性（Weiguo）' },
  { value: 'Xiaoyu', label: '女声-温柔（Xiaoyu）' },
  { value: 'Yuewen', label: '女声-甜美（Yuewen）' },
  { value: 'Lingyu', label: '女声-自然（Lingyu）' },
  { value: 'Trump', label: '英文男声（Trump）' },
  { value: 'Ava', label: '英文女声（Ava）' },
  { value: 'Bella', label: '英文女声（Bella）' },
  { value: 'Adam', label: '英文男声（Adam）' },
  { value: 'Nathan', label: '英文男声（Nathan）' },
  { value: 'Soyo', label: '日语女声（Soyo）' },
  { value: 'Saki', label: '日语女声（Saki）' },
  { value: 'Mortis', label: '日语女声（Mortis）' },
  { value: 'Umiri', label: '日语女声（Umiri）' },
  { value: 'Mei', label: '日语女声（Mei）' },
  { value: 'Anon', label: '日语女声（Anon）' },
  { value: 'Arisa', label: '日语女声（Arisa）' },
] as const

export type MossTtsVoice = (typeof MOSS_TTS_VOICE_OPTIONS)[number]['value']

export const MOSS_TTS_SUPPORTED_LANGUAGES = [
  'Chinese',
  'English',
  'Japanese',
  'Korean',
  'Spanish',
  'French',
  'German',
  'Italian',
  'Hungarian',
  'Russian',
  'Persian',
  'Arabic',
  'Polish',
  'Portuguese',
  'Czech',
  'Danish',
  'Swedish',
  'Greek',
  'Turkish',
] as const

export const MOSS_VOICE_CLONE_MIN_SECONDS = 2
export const MOSS_VOICE_CLONE_MAX_SECONDS = 15
export const MOSS_VOICE_CLONE_RECOMMENDED_SECONDS = '建议 3-10 秒'

function makeSafeFileNameSegment(text: string): string {
  const collapsed = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  return collapsed.slice(0, 32) || 'speech'
}

function createOutputFileName(text: string, voice: MossTtsVoice): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `ai-tts-${makeSafeFileNameSegment(text)}-${makeSafeFileNameSegment(voice)}-moss-${timestamp}.wav`
}

function createVoiceCloneOutputFileName(text: string): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `ai-tts-${makeSafeFileNameSegment(text)}-voice-clone-moss-${timestamp}.wav`
}

async function readAudioDurationSeconds(file: Blob): Promise<number> {
  if (typeof Audio === 'undefined') {
    return 0
  }

  const objectUrl = URL.createObjectURL(file)

  try {
    return await new Promise<number>((resolve) => {
      const audio = new Audio()
      audio.preload = 'metadata'
      audio.src = objectUrl
      audio.onloadedmetadata = () => resolve(Number.isFinite(audio.duration) ? audio.duration : 0)
      audio.onerror = () => resolve(0)
    })
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export async function inspectMossVoiceCloneReferenceAudio(file: File): Promise<{ duration: number }> {
  const duration = await readAudioDurationSeconds(file)

  if (duration <= 0) {
    throw new Error('无法读取参考音频时长，请重新选择音频文件。')
  }

  if (duration < MOSS_VOICE_CLONE_MIN_SECONDS) {
    throw new Error(`参考音频至少需要 ${MOSS_VOICE_CLONE_MIN_SECONDS} 秒。`)
  }

  if (duration > MOSS_VOICE_CLONE_MAX_SECONDS) {
    throw new Error(`参考音频请控制在 ${MOSS_VOICE_CLONE_MAX_SECONDS} 秒内。`)
  }

  return { duration }
}

function concatFloat32Arrays(chunks: Float32Array[]): Float32Array {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
  const merged = new Float32Array(totalLength)
  let offset = 0

  for (const chunk of chunks) {
    merged.set(chunk, offset)
    offset += chunk.length
  }

  return merged
}

function mergeChunkChannels(audioChunks: SerializedAudioChunk[]): {
  channels: Float32Array[]
  sampleRate: number
} {
  let sampleRate = 0
  let channelCount = 0
  const mergedChunks: Float32Array[][] = []

  for (const chunk of audioChunks) {
    const chunkChannels = chunk.buffers.map((buffer) => new Float32Array(buffer))
    if (chunkChannels.length === 0) {
      continue
    }

    if (sampleRate === 0) {
      sampleRate = chunk.sampleRate
    }

    if (channelCount === 0) {
      channelCount = chunk.channels || chunkChannels.length
      for (let index = 0; index < channelCount; index += 1) {
        mergedChunks.push([])
      }
    }

    const fallbackLength = chunkChannels[0]?.length ?? 0
    for (let channelIndex = 0; channelIndex < channelCount; channelIndex += 1) {
      mergedChunks[channelIndex]?.push(
        chunkChannels[channelIndex] ?? new Float32Array(fallbackLength),
      )
    }
  }

  return {
    channels: mergedChunks.map((channelChunks) => concatFloat32Arrays(channelChunks)),
    sampleRate,
  }
}

function createFloat32WavBlob(channels: Float32Array[], sampleRate: number): Blob {
  const channelCount = channels.length
  const frameCount = channels[0]?.length ?? 0
  const bytesPerSample = 4
  const blockAlign = channelCount * bytesPerSample
  const dataSize = frameCount * blockAlign
  const header = new ArrayBuffer(44)
  const view = new DataView(header)

  const writeString = (offset: number, value: string) => {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(offset + index, value.charCodeAt(index))
    }
  }

  writeString(0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true)
  writeString(8, 'WAVE')
  writeString(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 3, true)
  view.setUint16(22, channelCount, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * blockAlign, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, 32, true)
  writeString(36, 'data')
  view.setUint32(40, dataSize, true)

  const interleaved = new Float32Array(frameCount * channelCount)
  for (let frameIndex = 0; frameIndex < frameCount; frameIndex += 1) {
    for (let channelIndex = 0; channelIndex < channelCount; channelIndex += 1) {
      interleaved[frameIndex * channelCount + channelIndex] =
        channels[channelIndex]?.[frameIndex] ?? 0
    }
  }

  return new Blob([header, interleaved.buffer], { type: 'audio/wav' })
}

function getAudioDurationSeconds(channels: Float32Array[], sampleRate: number): number {
  const frameCount = channels[0]?.length ?? 0
  return sampleRate > 0 ? frameCount / sampleRate : 0
}

function applyPlaybackSpeed(channels: Float32Array[], speed: number): Float32Array[] {
  const normalizedSpeed = Number.isFinite(speed) ? Math.min(2, Math.max(0.5, speed)) : 1
  if (Math.abs(normalizedSpeed - 1) < 0.001) {
    return channels
  }

  return channels.map((channel) => {
    if (channel.length <= 1) {
      return channel
    }

    const outputLength = Math.max(1, Math.floor((channel.length - 1) / normalizedSpeed) + 1)
    const output = new Float32Array(outputLength)

    for (let index = 0; index < outputLength; index += 1) {
      const sourcePosition = index * normalizedSpeed
      const baseIndex = Math.floor(sourcePosition)
      const nextIndex = Math.min(channel.length - 1, baseIndex + 1)
      const fraction = sourcePosition - baseIndex
      const start = channel[baseIndex] ?? 0
      const end = channel[nextIndex] ?? start
      output[index] = start + (end - start) * fraction
    }

    return output
  })
}

export function getMossTtsVoiceOption(voice: MossTtsVoice): { value: MossTtsVoice; label: string } {
  return (
    MOSS_TTS_VOICE_OPTIONS.find((option) => option.value === voice) ?? {
      value: voice,
      label: voice,
    }
  )
}

class MossTtsService {
  private readonly runtimeFeature = 'moss-tts'
  private readonly runtimeFeatureLabel = '快剪多语言模型'
  private activeJobs = 0
  private generationChain: Promise<void> | null = null
  private pendingRequests = new Map<string, PendingRequest<ResponseInfo['data']>>()
  private pendingHostRequests = new Map<string, PendingRequest<ResponseInfo['data']>>()
  private worker: Worker | null = null
  private workerReadyPromise: Promise<void> | null = null
  private hostIframe: HTMLIFrameElement | null = null
  private hostWindow: Window | null = null
  private hostReadyPromise: Promise<void> | null = null
  private preparedPromise: Promise<void> | null = null
  private messageListenerAttached = false
  private hostReadyRejector: ((error: Error) => void) | null = null
  private workerReadyRejector: ((error: Error) => void) | null = null

  isSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof document !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      typeof navigator.storage?.getDirectory === 'function'
    )
  }

  private getRuntimeId(): string {
    return 'moss-tts:nano'
  }

  private upsertRuntime(
    state: 'loading' | 'running' | 'ready' | 'error',
    errorMessage?: string,
  ): void {
    const runtimeId = this.getRuntimeId()
    const existing = useLocalInferenceStore.getState().runtimesById[runtimeId]
    const now = Date.now()

    if (existing) {
      localInferenceRuntimeRegistry.updateRuntime(runtimeId, {
        feature: this.runtimeFeature,
        featureLabel: this.runtimeFeatureLabel,
        modelKey: MODEL_KEY,
        modelLabel: MODEL_LABEL,
        backend: 'wasm',
        state,
        estimatedBytes: ESTIMATED_BYTES,
        activeJobs: this.activeJobs,
        unloadable: true,
        errorMessage,
        lastUsedAt: now,
      })
      return
    }

    localInferenceRuntimeRegistry.registerRuntime(
      {
        id: runtimeId,
        feature: this.runtimeFeature,
        featureLabel: this.runtimeFeatureLabel,
        modelKey: MODEL_KEY,
        modelLabel: MODEL_LABEL,
        backend: 'wasm',
        state,
        estimatedBytes: ESTIMATED_BYTES,
        activeJobs: this.activeJobs,
        loadedAt: now,
        lastUsedAt: now,
        unloadable: true,
        errorMessage,
      },
      {
        unload: () => this.unload(),
      },
    )
  }

  private incrementJobs(): void {
    this.activeJobs += 1
    this.upsertRuntime('running')
  }

  private decrementJobs(): void {
    this.activeJobs = Math.max(0, this.activeJobs - 1)
    this.upsertRuntime('ready')
  }

  private attachMessageListener(): void {
    if (this.messageListenerAttached || typeof window === 'undefined') {
      return
    }
    window.addEventListener('message', this.handleHostMessage)
    this.messageListenerAttached = true
  }

  private readonly handleWorkerMessage = (
    event: MessageEvent<ReadyInfo | ProgressInfo | ResponseInfo>,
  ) => {
    const payload = event.data
    if (!payload || (payload as { source?: string }).source !== HOST_SOURCE) {
      return
    }

    if (payload.type === 'ready') {
      this.workerReadyResolver?.()
      return
    }

    if ('requestId' in payload) {
      const request = this.pendingRequests.get(payload.requestId)
      if (!request) {
        return
      }

      if (payload.type === 'progress') {
        request.onProgress?.(payload.stage || '正在准备快剪多语言模型...')
        return
      }

      this.pendingRequests.delete(payload.requestId)

      if (payload.ok) {
        request.resolve(payload.data)
      } else {
        request.reject(new Error(payload.error || '快剪多语言模型请求失败。'))
      }
    }
  }

  private readonly handleHostMessage = (
    event: MessageEvent<ReadyInfo | ProgressInfo | ResponseInfo & { source?: string }>,
  ) => {
    const payload = event.data as (ReadyInfo | ProgressInfo | ResponseInfo) & { source?: string }
    if (!payload || payload.source !== IFRAME_HOST_SOURCE) {
      return
    }

    if (payload.type === 'ready') {
      this.hostWindow = event.source as Window | null
      return
    }

    if ('requestId' in payload) {
      const request = this.pendingHostRequests.get(payload.requestId)
      if (!request) {
        return
      }

      if (payload.type === 'progress') {
        request.onProgress?.(payload.stage || '正在准备快剪多语言模型...')
        return
      }

      this.pendingHostRequests.delete(payload.requestId)

      if (payload.ok) {
        request.resolve(payload.data)
      } else {
        request.reject(new Error(payload.error || '快剪多语言模型宿主请求失败。'))
      }
    }
  }

  private async ensureHostLoaded(onProgress?: (stage: string) => void): Promise<void> {
    if (this.hostReadyPromise) {
      return this.hostReadyPromise
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new Error('当前环境无法使用快剪多语言模型语音克隆宿主页面。')
    }

    this.attachMessageListener()
    onProgress?.('正在启动快剪多语言模型宿主...')

    this.hostReadyPromise = new Promise<void>((resolve, reject) => {
      const iframe = document.createElement('iframe')
      const iframeUrl = buildMossAssetUrl(HOST_IFRAME_PATH)
      iframe.src = iframeUrl
      iframe.style.display = 'none'
      iframe.setAttribute('aria-hidden', 'true')

      const timeoutId = window.setTimeout(() => {
        this.hostReadyRejector?.(
          new Error(
            `启动快剪多语言模型宿主超时。iframe=${iframeUrl} crossOriginIsolated=${String(window.crossOriginIsolated)}`,
          ),
        )
      }, HOST_READY_TIMEOUT_MS)

      iframe.addEventListener('load', () => {
        this.hostWindow = iframe.contentWindow
        window.clearTimeout(timeoutId)
        this.hostReadyRejector = null
        resolve()
      })

      iframe.addEventListener('error', () => {
        this.hostReadyRejector?.(new Error(`快剪多语言模型宿主加载失败：${iframeUrl}`))
      })

      this.hostReadyRejector = (error) => {
        window.clearTimeout(timeoutId)
        this.hostReadyPromise = null
        this.hostReadyRejector = null
        iframe.remove()
        this.hostIframe = null
        this.hostWindow = null
        reject(error)
      }

      document.body.appendChild(iframe)
      this.hostIframe = iframe
    })

    return this.hostReadyPromise
  }

  private workerReadyResolver: (() => void) | null = null

  private async ensureWorkerLoaded(onProgress?: (stage: string) => void): Promise<void> {
    if (this.workerReadyPromise) {
      return this.workerReadyPromise
    }

    if (!this.isSupported()) {
      throw new Error('当前浏览器不支持浏览器托管存储。')
    }

    this.attachMessageListener()
    onProgress?.('正在启动快剪多语言模型工作线程...')

    this.workerReadyPromise = new Promise<void>((resolve, reject) => {
      const workerUrl = buildMossAssetUrl(WORKER_PATH)
      const worker = new Worker(workerUrl)
      this.worker = worker
      worker.addEventListener('message', this.handleWorkerMessage)
      worker.addEventListener('error', this.handleWorkerError)

      const timeoutId = window.setTimeout(() => {
        this.workerReadyRejector?.(
          new Error(
            `启动 MOSS 工作线程超时。worker=${workerUrl} crossOriginIsolated=${String(window.crossOriginIsolated)}`,
          ),
        )
      }, WORKER_READY_TIMEOUT_MS)

      this.workerReadyResolver = () => {
        window.clearTimeout(timeoutId)
        this.workerReadyResolver = null
        this.workerReadyRejector = null
        resolve()
      }

      this.workerReadyRejector = (error) => {
        window.clearTimeout(timeoutId)
        this.workerReadyResolver = null
        this.workerReadyRejector = null
        this.workerReadyPromise = null
        worker.terminate()
        this.worker = null
        reject(error)
      }
    })

    return this.workerReadyPromise
  }

  private readonly handleWorkerError = (event: ErrorEvent) => {
    const details = [event.message, event.filename, event.lineno ? `line ${event.lineno}` : '']
      .filter(Boolean)
      .join(' | ')

    this.workerReadyRejector?.(
      new Error(details ? `MOSS 工作线程启动失败：${details}` : 'MOSS 工作线程启动失败。'),
    )
  }

  private async requestWorker(
    action: 'warmup' | 'synthesize' | 'synthesizeClone' | 'dispose',
    payload: Record<string, unknown>,
    onProgress?: (stage: string) => void,
  ): Promise<ResponseInfo['data']> {
    await this.ensureWorkerLoaded(onProgress)

    const requestId = crypto.randomUUID()
    if (!this.worker) {
      throw new Error('MOSS 工作线程不可用。')
    }

    return new Promise<ResponseInfo['data']>((resolve, reject) => {
      this.pendingRequests.set(requestId, { resolve, reject, onProgress })

      this.worker?.postMessage({
        source: CLIENT_SOURCE,
        action,
        requestId,
        threadCount: THREAD_COUNT,
        ...payload,
      })
    })
  }

  private async requestHost(
    action: 'warmup' | 'synthesizeClone' | 'dispose',
    payload: Record<string, unknown>,
    onProgress?: (stage: string) => void,
  ): Promise<ResponseInfo['data']> {
    await this.ensureHostLoaded(onProgress)

    const requestId = crypto.randomUUID()
    if (!this.hostWindow) {
      throw new Error('MOSS 主线程宿主不可用。')
    }

    return new Promise<ResponseInfo['data']>((resolve, reject) => {
      this.pendingHostRequests.set(requestId, { resolve, reject, onProgress })

      this.hostWindow?.postMessage(
        {
          source: CLIENT_SOURCE,
          action,
          requestId,
          threadCount: THREAD_COUNT,
          ...payload,
        },
        '*',
      )
    })
  }

  private async ensurePrepared(onProgress?: (stage: string) => void): Promise<void> {
    if (this.preparedPromise) {
      return this.preparedPromise
    }

    this.upsertRuntime('loading')
    this.preparedPromise = this.requestWorker('warmup', {}, onProgress)
      .then(() => {
        this.upsertRuntime('ready')
      })
      .catch((error) => {
        this.preparedPromise = null
        const message = error instanceof Error ? error.message : String(error)
        this.upsertRuntime('error', message)
        throw error
      })

    return this.preparedPromise
  }

  private async withGenerationLock<T>(task: () => Promise<T>): Promise<T> {
    const previous = this.generationChain ?? Promise.resolve()
    let releaseCurrent = () => {}
    const current = new Promise<void>((resolve) => {
      releaseCurrent = resolve
    })
    const queued = previous.then(() => current)
    this.generationChain = queued

    await previous

    try {
      return await task()
    } finally {
      releaseCurrent()
      if (this.generationChain === queued) {
        this.generationChain = null
      }
    }
  }

  async unload(): Promise<void> {
    this.preparedPromise = null
    this.workerReadyPromise = null
    this.activeJobs = 0
    for (const request of this.pendingRequests.values()) {
      request.reject(new Error(LOCAL_INFERENCE_UNLOADED_MESSAGE))
    }
    this.pendingRequests.clear()

    if (this.worker) {
      this.worker.postMessage({
        source: CLIENT_SOURCE,
        action: 'dispose',
        requestId: crypto.randomUUID(),
      })
      this.worker.removeEventListener('message', this.handleWorkerMessage)
      this.worker.removeEventListener('error', this.handleWorkerError)
      this.worker.terminate()
      this.worker = null
    }

    if (this.hostWindow) {
      this.hostWindow.postMessage(
        {
          source: CLIENT_SOURCE,
          action: 'dispose',
          requestId: crypto.randomUUID(),
        },
        '*',
      )
    }
    this.hostIframe?.remove()
    this.hostIframe = null
    this.hostWindow = null
    this.hostReadyPromise = null
    for (const request of this.pendingHostRequests.values()) {
      request.reject(new Error(LOCAL_INFERENCE_UNLOADED_MESSAGE))
    }
    this.pendingHostRequests.clear()

    localInferenceRuntimeRegistry.unregisterRuntime(this.getRuntimeId())
  }

  async generateSpeechFile({
    text,
    voice,
    speed,
    onProgress,
  }: GenerateSpeechOptions): Promise<{ blob: Blob; file: File; duration: number }> {
    const trimmedText = text.trim()
    if (!trimmedText) {
      throw new Error('请输入要合成的文本。')
    }

    if (!this.isSupported()) {
      throw new Error('当前浏览器无法运行本地快剪多语言模型。')
    }

    return this.withGenerationLock(async () => {
      await this.ensurePrepared(onProgress)
      this.incrementJobs()

      try {
        onProgress?.('正在使用快剪多语言模型生成语音...')
        const response = await this.requestWorker(
          'synthesize',
          {
            text: trimmedText,
            voiceName: voice,
          },
          onProgress,
        )

        const audioChunks = response?.audioChunks ?? []
        if (audioChunks.length === 0) {
          throw new Error('快剪多语言模型没有返回音频。')
        }

        const mergedAudio = mergeChunkChannels(audioChunks)
        if (mergedAudio.channels.length === 0 || mergedAudio.sampleRate <= 0) {
          throw new Error('快剪多语言模型返回的音频数据无效。')
        }

        const spedUpChannels = applyPlaybackSpeed(mergedAudio.channels, speed)
        const blob = createFloat32WavBlob(spedUpChannels, mergedAudio.sampleRate)
        const file = new File([blob], createOutputFileName(trimmedText, voice), {
          type: 'audio/wav',
          lastModified: Date.now(),
        })

        return {
          blob,
          file,
          duration: getAudioDurationSeconds(spedUpChannels, mergedAudio.sampleRate),
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        logger.error('Failed to generate speech with MOSS TTS runtime', error)
        this.upsertRuntime('error', message)
        throw error
      } finally {
        this.decrementJobs()
      }
    })
  }

  async generateVoiceCloneFile({
    text,
    speed,
    referenceAudio,
    onProgress,
  }: GenerateVoiceCloneOptions): Promise<{ blob: Blob; file: File; duration: number }> {
    const trimmedText = text.trim()
    if (!trimmedText) {
      throw new Error('请输入要合成的文本。')
    }

    if (!this.isSupported()) {
      throw new Error('当前浏览器无法运行本地 MOSS 多语言 TTS。')
    }

    await inspectMossVoiceCloneReferenceAudio(referenceAudio)

    return this.withGenerationLock(async () => {
      await this.ensurePrepared(onProgress)
      this.incrementJobs()

      try {
        onProgress?.('正在处理参考音频并生成克隆语音...')
        const referenceAudioBuffer = await referenceAudio.arrayBuffer()
        const response = await this.requestHost(
          'synthesizeClone',
          {
            text: trimmedText,
            referenceAudioBuffer,
          },
          onProgress,
        )

        const audioChunks = response?.audioChunks ?? []
        if (audioChunks.length === 0) {
          throw new Error('MOSS 语音克隆没有返回音频。')
        }

        const mergedAudio = mergeChunkChannels(audioChunks)
        if (mergedAudio.channels.length === 0 || mergedAudio.sampleRate <= 0) {
          throw new Error('MOSS 语音克隆返回的音频数据无效。')
        }

        const spedUpChannels = applyPlaybackSpeed(mergedAudio.channels, speed)
        const blob = createFloat32WavBlob(spedUpChannels, mergedAudio.sampleRate)
        const file = new File([blob], createVoiceCloneOutputFileName(trimmedText), {
          type: 'audio/wav',
          lastModified: Date.now(),
        })

        return {
          blob,
          file,
          duration: getAudioDurationSeconds(spedUpChannels, mergedAudio.sampleRate),
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        logger.error('Failed to generate speech with MOSS voice clone runtime', error)
        this.upsertRuntime('error', message)
        throw error
      } finally {
        this.decrementJobs()
      }
    })
  }
}

export const mossTtsService = new MossTtsService()
